import { useState } from 'react';
import { Authenticated, useLogout, useGetIdentity } from '@refinedev/core';
import { OrganizationSwitcher, useClerk } from '@clerk/react';
import { Outlet, Navigate, Link as RouterLink, useLocation } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Toolbar,
  Typography,
  IconButton,
  Container,
  Collapse,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { SvgIconComponent } from '@mui/icons-material';
import type { AdminScope } from '@livinhome/shared';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import { NotificationBell } from './components/NotificationBell';
import { ClusterSwitcher } from './components/ClusterSwitcher';
import { EditProfileDialog, initialsOf } from './components/EditProfileDialog';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DashboardIcon from '@mui/icons-material/Dashboard';
import GroupsIcon from '@mui/icons-material/Groups';
import ApartmentIcon from '@mui/icons-material/Apartment';
import PeopleIcon from '@mui/icons-material/People';
import ForumIcon from '@mui/icons-material/Forum';
import CampaignIcon from '@mui/icons-material/Campaign';
import PaymentsIcon from '@mui/icons-material/Payments';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PaidIcon from '@mui/icons-material/Paid';
import DeckIcon from '@mui/icons-material/Deck';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import SecurityIcon from '@mui/icons-material/Security';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import LocalPoliceIcon from '@mui/icons-material/LocalPolice';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import PersonIcon from '@mui/icons-material/Person';
import ShieldIcon from '@mui/icons-material/Shield';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import ImageIcon from '@mui/icons-material/Image';
import DarkModeIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeIcon from '@mui/icons-material/LightModeOutlined';
import TranslateIcon from '@mui/icons-material/Translate';
import { useColorMode } from './theme';
import { useLanguage } from './i18n';

const DRAWER_WIDTH = 248;

/** A single navigable link in the sidebar. */
interface NavLeaf {
  label: string;
  to: string;
  icon: SvgIconComponent;
  /** Hide behind a permission flag (Phase 4+ adds these) */
  permission?: string;
  /** Restrict to these admin tiers (omit = all tiers). */
  scopes?: AdminScope[];
}

/** A collapsible parent that branches to related child links (no page of its own). */
interface NavGroup {
  label: string;
  icon: SvgIconComponent;
  children: NavLeaf[];
}

type NavEntry = NavLeaf | NavGroup;

const isGroup = (entry: NavEntry): entry is NavGroup => 'children' in entry;

// `label` holds an i18n key (see ./i18n); the sidebar renders it through t().
const NAV_ITEMS: NavEntry[] = [
  { label: 'nav.dashboard', to: '/dashboard', icon: DashboardIcon },
  {
    label: 'nav.peopleProperty',
    icon: GroupsIcon,
    children: [
      { label: 'nav.units', to: '/units', icon: ApartmentIcon, permission: 'can_manage_units' },
      { label: 'nav.residents', to: '/residents', icon: PeopleIcon, permission: 'can_manage_residents' },
    ],
  },
  {
    label: 'nav.billing',
    icon: PaymentsIcon,
    children: [
      { label: 'nav.fees', to: '/billing/fees', icon: PaidIcon, permission: 'can_manage_payments' },
      { label: 'nav.invoices', to: '/invoices', icon: ReceiptLongIcon, permission: 'can_manage_payments' },
      { label: 'nav.reports', to: '/reports', icon: AssessmentIcon, permission: 'can_manage_payments' },
    ],
  },
  {
    label: 'nav.amenities',
    icon: DeckIcon,
    children: [
      { label: 'nav.facilities', to: '/facilities', icon: MeetingRoomIcon, permission: 'can_manage_facilities' },
      { label: 'nav.bookings', to: '/bookings', icon: EventAvailableIcon, permission: 'can_approve_bookings' },
    ],
  },
  {
    label: 'nav.community',
    icon: ForumIcon,
    children: [
      { label: 'nav.announcements', to: '/announcements', icon: CampaignIcon, permission: 'can_post_announcements' },
      { label: 'nav.complaints', to: '/complaints', icon: ReportProblemIcon, permission: 'can_manage_complaints' },
      { label: 'nav.paymentReminders', to: '/reminders', icon: NotificationsActiveIcon, permission: 'can_manage_payments' },
    ],
  },
  {
    label: 'nav.securityOps',
    icon: SecurityIcon,
    children: [
      { label: 'nav.operations', to: '/operations', icon: LocalPoliceIcon, permission: 'can_view_emergency' },
    ],
  },
  { label: 'nav.dashboard', to: '/platform/dashboard', icon: DashboardIcon, scopes: ['PLATFORM'] },
  {
    label: 'nav.platform',
    icon: AdminPanelSettingsIcon,
    children: [
      { label: 'nav.developers', to: '/platform/developers', icon: GroupsIcon, scopes: ['PLATFORM'] },
      { label: 'nav.clusters', to: '/platform/clusters', icon: ApartmentIcon, scopes: ['PLATFORM'] },
      { label: 'nav.archived', to: '/platform/archived', icon: Inventory2Icon, scopes: ['PLATFORM'] },
    ],
  },
  {
    label: 'nav.userManagement',
    icon: ManageAccountsIcon,
    children: [
      { label: 'nav.developerManagers', to: '/users/developer-managers', icon: ManageAccountsIcon, scopes: ['PLATFORM'] },
      { label: 'nav.clusterAdmins', to: '/users/cluster-admins', icon: PersonIcon, scopes: ['PLATFORM', 'DEVELOPER'] },
      { label: 'nav.security', to: '/users/security', icon: ShieldIcon, scopes: ['PLATFORM', 'DEVELOPER', 'CLUSTER'] },
      { label: 'nav.residents', to: '/users/residents', icon: VpnKeyIcon, scopes: ['PLATFORM', 'DEVELOPER', 'CLUSTER'] },
    ],
  },
  {
    label: 'nav.residentApp',
    icon: PhoneAndroidIcon,
    children: [
      { label: 'nav.branding', to: '/resident-app/branding', icon: ImageIcon, scopes: ['DEVELOPER'] },
    ],
  },
];

export function ProtectedLayout() {
  return (
    <Authenticated key="protected-layout" redirectOnFail="/onboarding" fallback={<Navigate to="/onboarding" />}>
      <LayoutShell />
    </Authenticated>
  );
}

interface Identity {
  name?: string;
  email?: string;
  avatar?: string;
  permissions?: string[];
  admin_scope?: AdminScope;
}

function LayoutShell() {
  const theme = useTheme();
  const { mode, toggle: toggleColorMode } = useColorMode();
  const { lang, setLang, t } = useLanguage();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountAnchor, setAccountAnchor] = useState<HTMLElement | null>(null);
  const [langAnchor, setLangAnchor] = useState<HTMLElement | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  const { mutate: logout } = useLogout();
  const clerk = useClerk();
  const { data: identity, refetch: refetchIdentity } = useGetIdentity<Identity>();
  const location = useLocation();
  const permissions = new Set(identity?.permissions ?? []);
  const scope = identity?.admin_scope;
  // Platform admins are confined to the User Management + Platform areas — they
  // don't operate inside any single cluster (Dashboard, Billing, Community,
  // Resident-app branding, …).
  const PLATFORM_ALLOWED_PREFIXES = ['/users', '/platform'];
  const platformAllowed = (to: string) => PLATFORM_ALLOWED_PREFIXES.some((p) => to.startsWith(p));
  const leafVisible = (leaf: NavLeaf) =>
    (!leaf.permission || permissions.has(leaf.permission)) &&
    (!leaf.scopes || (!!scope && leaf.scopes.includes(scope))) &&
    (scope !== 'PLATFORM' || platformAllowed(leaf.to));

  const isActive = (to: string) => location.pathname.startsWith(to);
  const groupActive = (group: NavGroup) => group.children.some((c) => isActive(c.to));

  // Groups default to open when the active route is one of their children;
  // an explicit toggle (stored here) overrides that default.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const toggleGroup = (group: NavGroup) =>
    setOpenGroups((prev) => ({
      ...prev,
      [group.label]: !(prev[group.label] ?? groupActive(group)),
    }));

  // Enforce the platform limit at the route level: a platform admin who lands on
  // (or types) any non-allowed path is bounced to the User Management hub.
  if (scope === 'PLATFORM' && !platformAllowed(location.pathname)) {
    return <Navigate to="/platform/dashboard" replace />;
  }

  const renderLeaf = (item: NavLeaf, nested = false) => {
    const active = isActive(item.to);
    const Icon = item.icon;
    return (
      <ListItemButton
        key={item.to}
        component={RouterLink}
        to={item.to}
        selected={active}
        onClick={() => setMobileOpen(false)}
        sx={{ borderRadius: 1, mx: 1, mb: 0.5, pl: nested ? 3.5 : 2 }}
      >
        <ListItemIcon sx={{ minWidth: 36, color: active ? 'secondary.main' : 'inherit' }}>
          <Icon fontSize="small" />
        </ListItemIcon>
        <ListItemText
          primary={t(item.label)}
          primaryTypographyProps={{ fontWeight: active ? 600 : 400 }}
        />
      </ListItemButton>
    );
  };

  const renderGroup = (group: NavGroup) => {
    const children = group.children.filter(leafVisible);
    if (children.length === 0) return null;
    const active = groupActive(group);
    const open = openGroups[group.label] ?? active;
    const Icon = group.icon;
    return (
      <Box key={group.label}>
        <ListItemButton onClick={() => toggleGroup(group)} sx={{ borderRadius: 1, mx: 1, mb: 0.5 }}>
          <ListItemIcon sx={{ minWidth: 36, color: active ? 'secondary.main' : 'inherit' }}>
            <Icon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={t(group.label)}
            primaryTypographyProps={{ fontWeight: active ? 600 : 400 }}
          />
          <KeyboardArrowDownIcon
            fontSize="small"
            sx={{
              opacity: 0.7,
              transition: 'transform 0.2s',
              transform: open ? 'rotate(180deg)' : 'none',
            }}
          />
        </ListItemButton>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List disablePadding>{children.map((c) => renderLeaf(c, true))}</List>
        </Collapse>
      </Box>
    );
  };

  const navList = (
    <List sx={{ py: 1 }}>
      {NAV_ITEMS.map((entry) => {
        if (isGroup(entry)) return renderGroup(entry);
        if (!leafVisible(entry)) return null;
        return renderLeaf(entry);
      })}
    </List>
  );

  return (
    // App shell: full-width top bar, full-width bottom bar, and a middle row
    // that holds the sidebar + scrolling content. The sidebar spans only from
    // the bottom of the top bar to the top of the footer.
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <AppBar
        position="static"
        elevation={0}
        color="default"
        sx={{ bgcolor: 'background.paper', color: 'text.primary', borderBottom: 1, borderColor: 'divider' }}
      >
        <Toolbar sx={{ gap: 1 }}>
          {!isDesktop ? (
            <IconButton
              color="inherit"
              edge="start"
              onClick={() => setMobileOpen((open) => !open)}
              aria-label={t('common.openNav')}
            >
              <MenuIcon />
            </IconButton>
          ) : null}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
            <Box
              component="img"
              src="/logo.png"
              alt=""
              aria-hidden
              sx={{ width: 26, height: 26, borderRadius: 1.5, flexShrink: 0 }}
            />
            <Typography variant="h6" sx={{ fontWeight: 600, letterSpacing: '-0.2px' }}>
              LivinHome
            </Typography>
          </Box>
          {scope !== 'PLATFORM' ? <ClusterSwitcher /> : null}
          <OrganizationSwitcher afterSelectOrganizationUrl="/dashboard" afterCreateOrganizationUrl="/dashboard" />
          <IconButton color="inherit" onClick={(e) => setLangAnchor(e.currentTarget)} aria-label="language">
            <TranslateIcon />
          </IconButton>
          <Menu
            anchorEl={langAnchor}
            open={Boolean(langAnchor)}
            onClose={() => setLangAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem selected={lang === 'id'} onClick={() => { setLang('id'); setLangAnchor(null); }}>
              Indonesia
            </MenuItem>
            <MenuItem selected={lang === 'en'} onClick={() => { setLang('en'); setLangAnchor(null); }}>
              English
            </MenuItem>
          </Menu>
          <IconButton color="inherit" onClick={toggleColorMode} aria-label="toggle dark mode">
            {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
          <NotificationBell />
          <Button
            color="inherit"
            onClick={(e) => setAccountAnchor(e.currentTarget)}
            sx={{ ml: 1, textTransform: 'none', gap: 1 }}
            aria-label="account menu"
          >
            <Avatar
              src={identity?.avatar}
              sx={{ width: 28, height: 28, fontSize: 13 }}
            >
              {initialsOf(identity?.name) || undefined}
            </Avatar>
            {identity?.name ?? t('common.account')}
          </Button>
          <Menu
            anchorEl={accountAnchor}
            open={Boolean(accountAnchor)}
            onClose={() => setAccountAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem
              onClick={() => {
                setAccountAnchor(null);
                setEditOpen(true);
              }}
            >
              <ListItemIcon>
                <ManageAccountsIcon fontSize="small" />
              </ListItemIcon>
              {t('common.editProfile')}
            </MenuItem>
            <MenuItem
              onClick={() => {
                setAccountAnchor(null);
                clerk.openUserProfile();
              }}
            >
              <ListItemIcon>
                <ManageAccountsIcon fontSize="small" />
              </ListItemIcon>
              {t('common.manageAccount')}
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => {
                setAccountAnchor(null);
                logout();
              }}
            >
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              {t('common.logout')}
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      {/* Middle row: sidebar + content, fills the space between the two bars */}
      <Box sx={{ display: 'flex', flexGrow: 1, minHeight: 0 }}>
        {/* Mobile: temporary drawer toggled by the hamburger */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
          }}
        >
          <Toolbar />
          {navList}
        </Drawer>

        {/* Desktop: static sidebar, only as tall as this row (between the bars) */}
        <Box
          component="nav"
          aria-label="main navigation"
          sx={{
            display: { xs: 'none', md: 'block' },
            width: DRAWER_WIDTH,
            flexShrink: 0,
            borderRight: 1,
            borderColor: 'divider',
            overflowY: 'auto',
            bgcolor: 'background.paper',
          }}
        >
          {navList}
        </Box>

        {/* Main content — scrolls independently of the bars */}
        <Box component="main" sx={{ flexGrow: 1, minWidth: 0, overflowY: 'auto' }}>
          <Container maxWidth="lg" sx={{ py: 3 }}>
            <Outlet />
          </Container>
        </Box>
      </Box>

      {/* Full-width footer band, like the top bar: copyright centered, build id right */}
      <Box
        component="footer"
        sx={{
          bgcolor: 'background.paper',
          color: 'text.secondary',
          borderTop: 1,
          borderColor: 'divider',
          py: 1.5,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {/* left spacer keeps the copyright centered */}
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption">
          © {new Date().getFullYear()} PT Semesta Digital Integrasi
        </Typography>
        <Typography variant="caption" sx={{ flex: 1, textAlign: 'right', opacity: 0.85 }}>
          v{__APP_VERSION__}
        </Typography>
      </Box>

      <EditProfileDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        currentName={identity?.name}
        currentAvatar={identity?.avatar}
        onUpdated={() => refetchIdentity()}
      />
    </Box>
  );
}
